import {
    Drawer,
    Box,
    Typography,
    Button,
    IconButton,
    Paper,
    CircularProgress
} from "@mui/material";
import {
    Close as CloseIcon,
    AutoAwesome as AIIcon
} from "@mui/icons-material";
import { t as coachT } from "../dashboard/coach/coachTokens";

const AISummaryDialog = ({ open, onClose, form, clientName, loading = false, coach = false }) => {
    // Get summary from form data (real data from backend) or show pending message
    const getSummary = () => {
        // If still loading from API
        if (loading) {
            return {
                keyPoints: [],
                recommendations: [],
                isLoading: true
            };
        }

        // If form is pending or not completed
        if (form?.status === "pending") {
            return {
                keyPoints: [
                    "**טופס זה ממתין למילוי על ידי המתאמן.**",
                    "לאחר שהמתאמן ימלא את הטופס, הסיכום יופיע כאן אוטומטית."
                ],
                recommendations: [],
                isPending: true
            };
        }

        // If form is completed but AI summary not yet generated
        if (!form?.aiKeyPoints || form.aiKeyPoints.length === 0) {
            return {
                keyPoints: [
                    "**לא ניתן היה ליצור סיכום.**",
                    "נסה שוב מאוחר יותר."
                ],
                recommendations: [],
                isLoading: false
            };
        }

        // Return real AI-generated summary
        return {
            keyPoints: form.aiKeyPoints,
            recommendations: form.aiRecommendations || [],
            isPending: false,
            isLoading: false
        };
    };

    const summaryData = getSummary();
    const summary = summaryData.keyPoints;
    const recommendations = summaryData.recommendations;

    // Parse markdown bold syntax
    const parseMarkdown = (text) => {
        return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    };

    if (!form) return null;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            transitionDuration={0}
            PaperProps={{
                sx: {
                    width: "500px",
                    maxWidth: "100vw",
                    direction: "rtl"
                }
            }}
        >
            {/* Header */}
            <Box sx={{
                position: "sticky",
                top: 0,
                backgroundColor: "#FFFFFF",
                borderBottom: "1px solid #E2E8F0",
                padding: "16px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                zIndex: 1
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                        background: coach ? `linear-gradient(135deg, ${coachT.green} 0%, ${coachT.greenDark} 100%)` : "linear-gradient(135deg, #57837E 0%, #46706B 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                        <AIIcon sx={{ color: "#FFFFFF", fontSize: "20px" }} />
                    </Box>
                    <Box>
                        <Typography sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#1E293B",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}>
                            סיכום AI לטופס
                        </Typography>
                        <Typography sx={{
                            fontSize: "13px",
                            color: "#64748B",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}>
                            {form?.title}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: "#64748B" }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ padding: "20px", flex: 1, overflow: "auto" }}>
                {/* Info Card */}
                <Paper
                    sx={{
                        padding: "16px",
                        background: "linear-gradient(135deg, rgba(87, 131, 126, 0.05) 0%, rgba(87, 131, 126, 0.1) 100%)",
                        border: "1px solid rgba(87, 131, 126, 0.2)",
                        borderRadius: "8px",
                        marginBottom: "24px"
                    }}
                    elevation={0}
                >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                        <AIIcon sx={{ color: coach ? coachT.green : "#57837E", fontSize: "20px", marginTop: "2px", flexShrink: 0 }} />
                        <Typography sx={{
                            fontSize: "13px",
                            color: "#64748B",
                            lineHeight: 1.6,
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}>
                            <strong>הסיכום הבא נוצר באמצעות בינה מלאכותית</strong> על סמך התשובות שמילא {clientName} בטופס.
                            הסיכום מדגיש את הנקודות המרכזיות והרלוונטיות ביותר לבניית תוכנית אימונים מותאמת אישית.
                        </Typography>
                    </Box>
                </Paper>

                {/* Loading indicator */}
                {summaryData.isLoading ? (
                    <Box sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "200px",
                        gap: 2
                    }}>
                        <CircularProgress size={40} sx={{ color: coach ? coachT.green : "#57837E" }} />
                        <Typography sx={{
                            fontSize: "14px",
                            color: "#64748B",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}>
                            יוצר סיכום AI...
                        </Typography>
                    </Box>
                ) : (
                    /* Summary Content */
                    <Box sx={{ marginBottom: "24px" }}>
                        <Typography sx={{
                            fontSize: "15px",
                            fontWeight: 600,
                            color: "#1E293B",
                            marginBottom: "16px",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}>
                            נקודות מרכזיות:
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {summary.map((point, index) => (
                                <Box key={index} sx={{ display: "flex", gap: 2 }}>
                                    <Box sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        backgroundColor: coach ? coachT.green : "#57837E",
                                        marginTop: "6px",
                                        flexShrink: 0
                                    }} />
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            color: "#64748B",
                                            lineHeight: 1.6,
                                            fontFamily: '"Heebo", "Roboto", sans-serif',
                                            "& strong": {
                                                color: "#1E293B",
                                                fontWeight: 600
                                            }
                                        }}
                                        dangerouslySetInnerHTML={{ __html: parseMarkdown(point) }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Recommendations Section */}
                {!summaryData.isLoading && recommendations.length > 0 && (
                    <Box sx={{
                        paddingTop: "24px",
                        borderTop: "1px solid #E2E8F0"
                    }}>
                        <Typography sx={{
                            fontSize: "15px",
                            fontWeight: 600,
                            color: "#1E293B",
                            marginBottom: "16px",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}>
                            המלצות לפעולה:
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {recommendations.map((rec, index) => (
                                <Paper
                                    key={index}
                                    sx={{
                                        padding: "12px 16px",
                                        backgroundColor: "#EFF6FF",
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 2
                                    }}
                                    elevation={0}
                                >
                                    <Typography sx={{ color: "#3B82F6", fontSize: "14px" }}>✓</Typography>
                                    <Typography sx={{
                                        fontSize: "13px",
                                        color: "#64748B",
                                        lineHeight: 1.5,
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    }}>
                                        {rec}
                                    </Typography>
                                </Paper>
                            ))}
                        </Box>

                        {/* AI Disclaimer */}
                        <Paper
                            sx={{
                                marginTop: "16px",
                                padding: "12px 16px",
                                backgroundColor: "#F8FAFC",
                                borderRadius: "8px"
                            }}
                            elevation={0}
                        >
                            <Typography sx={{
                                fontSize: "12px",
                                color: "#94A3B8",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}>
                                ההמלצה מופקת באמצעות AI ואינה מהווה ייעוץ מקצועי. האחריות על השימוש במידע היא על המאמן בלבד.
                            </Typography>
                        </Paper>
                    </Box>
                )}
            </Box>

            {/* Footer Actions */}
            <Box sx={{
                position: "sticky",
                bottom: 0,
                backgroundColor: "#FFFFFF",
                borderTop: "1px solid #E2E8F0",
                padding: "16px 20px",
                display: "flex",
                justifyContent: "center"
            }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        minWidth: "120px",
                        borderColor: "#E2E8F0",
                        color: "#64748B",
                        fontFamily: '"Heebo", "Roboto", sans-serif',
                        "&:hover": {
                            borderColor: "#CBD5E1",
                            backgroundColor: "#F8FAFC"
                        }
                    }}
                >
                    סגור
                </Button>
            </Box>
        </Drawer>
    );
};

export default AISummaryDialog;
